import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { type ConferenceInput, conferencesApi } from './conferences';

export const conferenceKeys = {
  all: ['conferences'] as const,
  list: () => ['conferences', 'list'] as const,
  detail: (id: string) => ['conferences', 'detail', id] as const,
  schedule: (id: string) => ['conferences', 'schedule', id] as const,
  mine: () => ['conferences', 'mine'] as const,
  calendarLink: () => ['conferences', 'calendar-link'] as const,
};

export function useConferencesQuery() {
  return useQuery({ queryKey: conferenceKeys.list(), queryFn: () => conferencesApi.list() });
}

export function useConferenceQuery(id: string | undefined) {
  return useQuery({
    queryKey: conferenceKeys.detail(id ?? ''),
    queryFn: () => conferencesApi.get(id!),
    enabled: !!id,
  });
}

export function useConferenceScheduleQuery(id: string | undefined) {
  return useQuery({
    queryKey: conferenceKeys.schedule(id ?? ''),
    queryFn: () => conferencesApi.schedule(id!),
    enabled: !!id,
  });
}

export function useMyConferencesQuery() {
  return useQuery({ queryKey: conferenceKeys.mine(), queryFn: () => conferencesApi.mine() });
}

export function useSaveMyConferenceNoteMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingId, note }: { bookingId: string; note: string | null }) =>
      conferencesApi.saveMyNote(bookingId, note),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: conferenceKeys.mine() }),
  });
}

export function useCreateConferenceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ConferenceInput) => conferencesApi.create(input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: conferenceKeys.all }),
  });
}

export function useUpdateConferenceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<ConferenceInput> }) => conferencesApi.update(id, input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: conferenceKeys.all }),
  });
}

export function useResyncConferenceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => conferencesApi.resync(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: conferenceKeys.all }),
  });
}

export function useAddConferenceTeacherMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, teacherId }: { id: string; teacherId: string }) => conferencesApi.addTeacher(id, teacherId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: conferenceKeys.all }),
  });
}

export function useUpdateConferenceTeacherMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ctId, input }: { id: string; ctId: string; input: { room?: string; subjectLabel?: string } }) =>
      conferencesApi.updateTeacher(id, ctId, input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: conferenceKeys.all }),
  });
}

export function useRemoveConferenceTeacherMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ctId }: { id: string; ctId: string }) => conferencesApi.removeTeacher(id, ctId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: conferenceKeys.all }),
  });
}

export function useSetConferenceBreaksMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ctId, startsAt }: { id: string; ctId: string; startsAt: string[] }) =>
      conferencesApi.setBreaks(id, ctId, startsAt),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: conferenceKeys.all }),
  });
}

export function useBookConferenceSlotMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: { conferenceTeacherId: string; startsAt: string; studentIds: string[] };
    }) => conferencesApi.book(id, input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: conferenceKeys.all }),
  });
}

export function useMoveConferenceBookingMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, bookingId, startsAt }: { id: string; bookingId: string; startsAt: string }) =>
      conferencesApi.move(id, bookingId, startsAt),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: conferenceKeys.all }),
  });
}

export function useCancelConferenceBookingMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, bookingId }: { id: string; bookingId: string }) => conferencesApi.cancelBooking(id, bookingId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: conferenceKeys.all }),
  });
}

export function useConferenceCandidatesQuery(id: string | undefined, studentId: string | undefined) {
  return useQuery({
    queryKey: ['conferences', 'candidates', id ?? '', studentId ?? ''] as const,
    queryFn: () => conferencesApi.candidates(id!, studentId!),
    enabled: !!id && !!studentId,
  });
}

export function useInviteConferenceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => conferencesApi.invite(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: conferenceKeys.all }),
  });
}

export function useRemindConferenceMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => conferencesApi.remind(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: conferenceKeys.all }),
  });
}

export function useCancelConferenceEventMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => conferencesApi.cancelEvent(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: conferenceKeys.all }),
  });
}

export function useCalendarLinkQuery() {
  return useQuery({ queryKey: conferenceKeys.calendarLink(), queryFn: () => conferencesApi.calendarLink() });
}

export function useEnsureCalendarLinkMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => conferencesApi.ensureCalendarLink(),
    onSuccess: data => queryClient.setQueryData(conferenceKeys.calendarLink(), data),
  });
}

export function useResetCalendarLinkMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => conferencesApi.resetCalendarLink(),
    onSuccess: data => queryClient.setQueryData(conferenceKeys.calendarLink(), data),
  });
}
